import React, { useState, useEffect } from 'react';
import { ArrowLeft, CreditCard, DollarSign, Mail } from 'lucide-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Phone } from 'lucide-react';
import apiService from './services/api';
import { Panel, Section, Button, Badge, LoadingSpinner, CallModal, AddCardModal, CheckoutModal } from './components';

function AppointmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [additionalNote, setAdditionalNote] = useState('');
  const [showCallModal, setShowCallModal] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [showAdditionalPayments, setShowAdditionalPayments] = useState(false);
  const [showFindPayment, setShowFindPayment] = useState(false);
  const [candidateTransactions, setCandidateTransactions] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [editingPayouts, setEditingPayouts] = useState({});
  const [savingPayout, setSavingPayout] = useState(null);
  const [showAddTechnician, setShowAddTechnician] = useState(false);
  const [newEventTech, setNewEventTech] = useState({ technician_id: '', payout: '' });
  const [savingNewEventTech, setSavingNewEventTech] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeTip, setChargeTip] = useState('');
  const [charging, setCharging] = useState(false);
  const [chargeResult, setChargeResult] = useState(null);
  const [internalNotes, setInternalNotes] = useState('');
  const [isSavingInternal, setIsSavingInternal] = useState(false);
  const [showAddService, setShowAddService] = useState(false);
  const [allServicesList, setAllServicesList] = useState([]);
  const [addServiceId, setAddServiceId] = useState('');
  const [addServiceQty, setAddServiceQty] = useState(1);
  const [addingService, setAddingService] = useState(false);
  const [showCustomService, setShowCustomService] = useState(false);
  const [addServiceMode, setAddServiceMode] = useState('catalog');
  const [customService, setCustomService] = useState({ description: '', cost: '', split: '' });
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [receiptSent, setReceiptSent] = useState(false);
  const [receiptError, setReceiptError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showLinkBooking, setShowLinkBooking] = useState(false);
  const [unlinkedBookings, setUnlinkedBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [linkingBooking, setLinkingBooking] = useState(false);
  const [chargingBalance, setChargingBalance] = useState(false);
  const [balanceTip, setBalanceTip] = useState('');
  const [showGiftCards, setShowGiftCards] = useState(false);
  const [unredeemedGiftCards, setUnredeemedGiftCards] = useState([]);
  const [loadingGiftCards, setLoadingGiftCards] = useState(false);
  const [applyingGiftCard, setApplyingGiftCard] = useState(false);

  useEffect(() => {
    if (id) {
      fetchAppointmentDetails();
    }
  }, [id]);

  const fetchAppointmentDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAppointment(id);
      setAppointment(data);
      setAdditionalNote(data.additional_note || '');
      setInternalNotes(data.internal_notes || '');
    } catch (err) {
      setError(err.message || 'Failed to fetch appointment details');
      console.error('Error fetching appointment:', err);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = async () => {
    // Load technician list for dropdown
    if (allTechnicians.length === 0) {
      try {
        const data = await apiService.get('/amn/staff/?is_technician=true&page_size=100');
        setAllTechnicians((data.results || data).filter(t => t.internal_status !== 'inactive'));
      } catch (err) { console.error('Failed to load technicians', err); }
    }
    // Pre-fill form with current values
    const dt = appointment.date_time ? new Date(appointment.date_time) : new Date();
    const pad = n => String(n).padStart(2, '0');
    setEditForm({
      date: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`,
      time: `${pad(dt.getHours())}:${pad(dt.getMinutes())}`,
      technician_id: appointment.technician?.id || '',
      address: appointment.address || '',
      city: appointment.city || '',
      state: appointment.state || 'FL',
      zip_code: appointment.zip_code || '',
      is_alf: appointment.is_alf || false,
      is_event: appointment.is_event || false,
    });
    setIsEditing(true);
  };

  const saveEdit = async () => {
    try {
      setSavingEdit(true);
      const dateTime = new Date(`${editForm.date}T${editForm.time}:00`);
      await apiService.updateAppointment(id, {
        date_time: dateTime.toISOString(),
        technician_id: editForm.technician_id || null,
        address: editForm.address,
        city: editForm.city,
        state: editForm.state,
        zip_code: editForm.zip_code,
        is_alf: editForm.is_alf,
        is_event: editForm.is_event,
      });
      setIsEditing(false);
      fetchAppointmentDetails();
    } catch (err) {
      alert('Failed to save: ' + (err.message || 'Unknown error'));
    } finally {
      setSavingEdit(false);
    }
  };

  const saveNote = async () => {
    try {
      setIsSavingNote(true);
      await apiService.updateAppointment(id, { additional_note: additionalNote });
      alert('Note saved successfully!');
      fetchAppointmentDetails();
    } catch (err) {
      alert('Failed to save note: ' + err.message);
    } finally {
      setIsSavingNote(false);
    }
  };

  const saveInternalNotes = async () => {
    try {
      setIsSavingInternal(true);
      await apiService.updateAppointment(id, { internal_notes: internalNotes });
      fetchAppointmentDetails();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setIsSavingInternal(false);
    }
  };

  // Event Technician handlers
  const fetchTechnicians = async () => {
    if (technicians.length > 0) return;
    try {
      const data = await apiService.getTechnicians();
      setTechnicians(data.results || data);
    } catch (err) {
      console.error('Failed to fetch technicians:', err);
    }
  };

  const saveEventTechPayout = async (eventTechId, payout) => {
    try {
      setSavingPayout(eventTechId);
      await apiService.updateEventTechnician(eventTechId, { payout: parseFloat(payout) });
      fetchAppointmentDetails();
    } catch (err) {
      alert('Failed to save payout: ' + err.message);
    } finally {
      setSavingPayout(null);
    }
  };

  const removeEventTechnician = async (eventTechId) => {
    if (!window.confirm('Remove this team member from the event?')) return;
    try {
      await apiService.deleteEventTechnician(eventTechId);
      fetchAppointmentDetails();
    } catch (err) {
      alert('Failed to remove team member: ' + err.message);
    }
  };

  const addEventTechnician = async () => {
    if (!newEventTech.technician_id || !newEventTech.payout) {
      alert('Please select a team member and enter a payout amount.');
      return;
    }
    try {
      setSavingNewEventTech(true);
      await apiService.createEventTechnician({
        appointment_id: parseInt(id),
        technician_id: parseInt(newEventTech.technician_id),
        payout: parseFloat(newEventTech.payout),
      });
      setNewEventTech({ technician_id: '', payout: '' });
      setShowAddTechnician(false);
      fetchAppointmentDetails();
    } catch (err) {
      alert('Failed to add team member: ' + err.message);
    } finally {
      setSavingNewEventTech(false);
    }
  };

  const cancelAppointment = async () => {
    const isSquare = !!appointment?.square_id;
    const message = isSquare
      ? 'This appointment is from Square. Cancelling here will also cancel it in Square.\n\nAre you sure?'
      : 'This appointment was created in the Dashboard (not in Square).\n\nAre you sure you want to cancel?';
    if (window.confirm(message)) {
      try {
        await apiService.cancelAppointment(id);
        alert(isSquare
          ? 'Appointment cancelled in Dashboard and Square.'
          : 'Appointment cancelled in Dashboard.');
        navigate('/appointments');
      } catch (err) {
        alert('Failed to cancel appointment: ' + err.message);
      }
    }
  };

  const matchTransaction = async (transactionId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/amn/appointments/${id}/link_transaction/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transaction_id: transactionId })
      });
      if (response.ok) {
        alert('Transaction matched successfully!');
        fetchAppointmentDetails();
      } else {
        alert('Failed to match transaction');
      }
    } catch (err) {
      alert('Error matching transaction: ' + err.message);
    }
  };

  const unmatchTransaction = async (transactionId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/amn/transactions/${transactionId}/unmatch_appointment/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        alert('Transaction unmatched successfully!');
        fetchAppointmentDetails();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert('Failed to unmatch transaction: ' + (errorData.error || response.statusText));
      }
    } catch (err) {
      alert('Error unmatching transaction: ' + err.message);
    }
  };

  const fetchCandidateTransactions = async () => {
    try {
      setLoadingCandidates(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/amn/appointments/${id}/unmatched_transactions/`);
      if (response.ok) {
        const data = await response.json();
        setCandidateTransactions(data.transactions || []);
        setShowFindPayment(true);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to fetch candidate transactions');
      }
    } catch (err) {
      alert('Error fetching transactions: ' + err.message);
    } finally {
      setLoadingCandidates(false);
    }
  };

  const linkTransaction = async (transactionId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/amn/appointments/${id}/link_transaction/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transaction_id: transactionId })
      });
      if (response.ok) {
        alert('Payment linked successfully!');
        setShowFindPayment(false);
        setCandidateTransactions([]);
        fetchAppointmentDetails();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to link transaction');
      }
    } catch (err) {
      alert('Error linking transaction: ' + err.message);
    }
  };

  const attributeDifference = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/amn/appointments/${id}/attribute_difference/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const result = await response.json();
        alert(result.message || 'Difference attributed successfully!');
        fetchAppointmentDetails();
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to attribute difference');
      }
    } catch (err) {
      alert('Error attributing difference: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        {/* Header with Back Button */}
        <div className="mb-3 flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => navigate('/appointments')}>
            <ArrowLeft className="w-3.5 h-3.5 mr-1" />
            Back
          </Button>
          <h1 className="text-lg font-semibold text-gray-900 leading-none">Appointment Details</h1>
        </div>

        {/* Loading State */}
        {loading && (
          <Panel className="p-6">
            <LoadingSpinner />
          </Panel>
        )}

        {/* Error State */}
        {error && !loading && (
          <Panel className="bg-red-50 border-red-200 mb-3">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-4 w-4 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-2">
                <h3 className="text-xs font-medium text-red-800">Error loading appointment</h3>
                <p className="mt-0.5 text-xs text-red-700">{error}</p>
                <button
                  onClick={fetchAppointmentDetails}
                  className="mt-1 text-xs font-medium text-red-800 hover:text-red-900"
                >
                  Try again
                </button>
              </div>
            </div>
          </Panel>
        )}

        {/* Content */}
        {!loading && !error && appointment && (
          <div className="space-y-2">
            {/* Basic Information & People */}
            <Panel>
              <div className="space-y-2">
                <Section title="Appointment Details">
                  {isEditing ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                        <div>
                          <label className="block text-xs text-gray-500 mb-0.5">Date</label>
                          <input type="date" value={editForm.date} onChange={e => setEditForm({...editForm, date: e.target.value})}
                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-0.5">Time</label>
                          <input type="time" value={editForm.time} onChange={e => setEditForm({...editForm, time: e.target.value})}
                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-0.5">Technician</label>
                          <select value={editForm.technician_id} onChange={e => setEditForm({...editForm, technician_id: e.target.value})}
                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800">
                            <option value="">Select technician</option>
                            {allTechnicians.map(t => (
                              <option key={t.id} value={t.id}>{t.person?.first_name} {t.person?.last_name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-0.5">Type</label>
                          <select value={editForm.is_alf ? 'alf' : editForm.is_event ? 'event' : 'standard'}
                            onChange={e => setEditForm({...editForm, is_alf: e.target.value === 'alf', is_event: e.target.value === 'event'})}
                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800">
                            <option value="standard">Standard</option>
                            <option value="alf">ALF</option>
                            <option value="event">Event</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-0.5">Address</label>
                          <input type="text" value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})}
                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-0.5">City</label>
                          <input type="text" value={editForm.city} onChange={e => setEditForm({...editForm, city: e.target.value})}
                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-0.5">State</label>
                          <input type="text" value={editForm.state} onChange={e => setEditForm({...editForm, state: e.target.value})}
                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-0.5">Zip</label>
                          <input type="text" value={editForm.zip_code} onChange={e => setEditForm({...editForm, zip_code: e.target.value})}
                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800" />
                        </div>
                      </div>
                      {/* Services editing inline */}
                      <div className="border-t border-gray-200 pt-2 mt-2">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-medium text-gray-700">Services</p>
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={async () => {
                              if (allServicesList.length === 0) {
                                try {
                                  const data = await apiService.get('/amn/services/', { active: true, ordering: 'name', page_size: 100 });
                                  setAllServicesList(data.results || data);
                                } catch (err) { console.error(err); }
                              }
                              setShowAddService(!showAddService);
                              setShowCustomService(false);
                            }} className="text-[10px] text-red-700 hover:text-red-900 font-medium">+ Add</button>
                            <button type="button" onClick={() => {
                              setShowCustomService(!showCustomService);
                              setShowAddService(false);
                            }} className="text-[10px] text-blue-700 hover:text-blue-900 font-medium">+ Custom</button>
                          </div>
                        </div>
                        {showAddService && (
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <select value={addServiceId} onChange={e => setAddServiceId(e.target.value)}
                              className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded">
                              <option value="">Select service...</option>
                              {allServicesList.map(s => (
                                <option key={s.id} value={s.id}>{s.name} (${parseFloat(s.price || 0).toFixed(2)})</option>
                              ))}
                            </select>
                            <div className="flex items-center gap-1">
                              <button type="button" onClick={() => setAddServiceQty(Math.max(1, addServiceQty - 1))} className="w-5 h-5 rounded bg-gray-200 text-xs flex items-center justify-center">−</button>
                              <span className="text-xs font-semibold w-4 text-center">{addServiceQty}</span>
                              <button type="button" onClick={() => setAddServiceQty(addServiceQty + 1)} className="w-5 h-5 rounded bg-gray-200 text-xs flex items-center justify-center">+</button>
                            </div>
                            <button type="button" disabled={!addServiceId || addingService} onClick={async () => {
                              setAddingService(true);
                              try {
                                await apiService.post(`/amn/appointments/${id}/add-service/`, { service_id: parseInt(addServiceId), quantity: addServiceQty });
                                setShowAddService(false); setAddServiceId(''); setAddServiceQty(1);
                                fetchAppointmentDetails();
                              } catch (err) { alert('Failed: ' + err.message); }
                              finally { setAddingService(false); }
                            }} className="px-2 py-1 text-xs bg-red-800 text-white rounded disabled:opacity-50">
                              {addingService ? '...' : 'Add'}
                            </button>
                          </div>
                        )}
                        {showCustomService && (
                          <div className="bg-blue-50 border border-blue-200 rounded-md p-2 mb-2 space-y-1.5">
                            <p className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide">Custom Service</p>
                            <input type="text" value={customService.description}
                              onChange={e => setCustomService({...customService, description: e.target.value})}
                              placeholder="Description (e.g. Toenail restoration)"
                              className="w-full px-2 py-1 text-xs border border-blue-200 rounded focus:ring-1 focus:ring-blue-500" />
                            <div className="grid grid-cols-2 gap-1.5">
                              <div>
                                <label className="text-[9px] text-blue-600 font-medium">Client Cost</label>
                                <input type="number" step="0.01" value={customService.cost}
                                  onChange={e => setCustomService({...customService, cost: e.target.value})}
                                  placeholder="$0.00"
                                  className="w-full px-2 py-1 text-xs border border-blue-200 rounded focus:ring-1 focus:ring-blue-500" />
                              </div>
                              <div>
                                <label className="text-[9px] text-blue-600 font-medium">Team Member Split</label>
                                <input type="number" step="0.01" value={customService.split}
                                  onChange={e => setCustomService({...customService, split: e.target.value})}
                                  placeholder="$0.00"
                                  className="w-full px-2 py-1 text-xs border border-blue-200 rounded focus:ring-1 focus:ring-blue-500" />
                              </div>
                            </div>
                            <div className="flex gap-1.5">
                              <button type="button" disabled={!customService.description || !customService.cost || addingService}
                                onClick={async () => {
                                  setAddingService(true);
                                  try {
                                    await apiService.post(`/amn/appointments/${id}/add-custom-service/`, {
                                      description: customService.description,
                                      cost: parseFloat(customService.cost),
                                      split_amount: parseFloat(customService.split || 0),
                                    });
                                    setShowCustomService(false);
                                    setCustomService({ description: '', cost: '', split: '' });
                                    fetchAppointmentDetails();
                                  } catch (err) { alert('Failed: ' + err.message); }
                                  finally { setAddingService(false); }
                                }}
                                className="flex-1 px-2 py-1 text-xs bg-blue-700 text-white rounded disabled:opacity-50 hover:bg-blue-800">
                                {addingService ? '...' : 'Add Custom Service'}
                              </button>
                              <button type="button" onClick={() => setShowCustomService(false)}
                                className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                            </div>
                          </div>
                        )}
                        {appointment.services?.map((svc, idx) => (
                          <div key={svc.id || idx} className="flex items-center justify-between py-0.5 text-xs">
                            <span className="text-gray-700">{svc.service_name || 'Unknown'}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500">${parseFloat(svc.service_cost || 0).toFixed(2)}</span>
                              <button type="button" onClick={async () => {
                                if (window.confirm('Remove this service?')) {
                                  await apiService.post(`/amn/appointments/${id}/remove-service/`, { service_entry_id: svc.id });
                                  fetchAppointmentDetails();
                                }
                              }} className="text-gray-300 hover:text-red-500">✕</button>
                            </div>
                          </div>
                        ))}
                        <div className="text-xs font-semibold text-right mt-1 text-gray-900">
                          Total: ${appointment.services?.reduce((sum, s) => sum + parseFloat(s.service_cost || 0), 0).toFixed(2)}
                        </div>
                      </div>

                      <div className="flex gap-2 pt-1">
                        <Button size="sm" onClick={saveEdit} disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save'}</Button>
                        <Button size="sm" variant="secondary" onClick={() => setIsEditing(false)}>Cancel</Button>
                        {appointment.square_id && <span className="text-[10px] text-gray-400 self-center">Changes will sync to Square</span>}
                      </div>
                    </div>
                  ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Status</p>
                      {(appointment.internal_status === 'completed' || appointment.status?.toUpperCase() === 'COMPLETED') ? (
                        <Badge variant="success" small>Completed</Badge>
                      ) : (
                        <Badge variant="neutral" small>{appointment.status}</Badge>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Date</p>
                      <p className="text-xs font-medium text-gray-900">{formatDate(appointment.date_time)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Time</p>
                      <p className="text-xs font-medium text-gray-900">{formatTime(appointment.date_time)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Type</p>
                      <Badge variant="neutral" small>
                        {appointment.appointment_type || (appointment.is_alf ? 'ALF' : appointment.is_event ? 'Event' : 'Standard')}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Review</p>
                      {appointment.has_review ? (
                        <Badge variant="success" small>Yes</Badge>
                      ) : (
                        <Badge variant="neutral" small>No</Badge>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">ETA Sent</p>
                      {appointment.eta_sent ? (
                        <div>
                          <Badge variant="success" small>{appointment.eta_minutes ? `${appointment.eta_minutes} min` : 'Yes'}</Badge>
                          {appointment.eta_sent_at && (
                            <p className="text-[10px] text-gray-400 mt-0.5">{new Date(appointment.eta_sent_at).toLocaleString()}</p>
                          )}
                        </div>
                      ) : (
                        <Badge variant="neutral" small>No</Badge>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Booked By</p>
                      <p className="text-xs font-medium text-gray-900">
                        {appointment.booked_by_name || (appointment.square_id ? 'Square' : 'Unknown')}
                      </p>
                      {appointment.booking_source && (
                        <span className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded mt-0.5 ${
                          appointment.booking_source === 'square' ? 'bg-blue-50 text-blue-700' :
                          appointment.booking_source === 'online' ? 'bg-green-50 text-green-700' :
                          appointment.booking_source === 'phone' ? 'bg-purple-50 text-purple-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          via {appointment.booking_source}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Created</p>
                      <p className="text-xs font-medium text-gray-900">
                        {appointment.creation_date ? new Date(appointment.creation_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {appointment.creation_date ? new Date(appointment.creation_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : ''}
                      </p>
                    </div>
                  </div>
                  )}
                  {!isEditing && (
                    <div className="mt-2">
                      <Button size="sm" variant="secondary" onClick={startEditing}>Edit Appointment</Button>
                    </div>
                  )}
                </Section>

                <div className="border-t border-gray-200 pt-2">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Client</p>
                      <Link to={`/clients/${appointment.client?.id}`} className="text-xs font-medium text-red-800 hover:underline">
                        {appointment.client?.person?.first_name} {appointment.client?.person?.last_name}
                      </Link>
                      {appointment.client?.person?.phone_number && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <p className="text-xs text-gray-600">{appointment.client.person.phone_number}</p>
                          <button
                            onClick={() => setShowCallModal(true)}
                            className="text-green-600 hover:text-green-800 transition-colors"
                            title="Call client"
                          >
                            <Phone className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      {appointment.client?.person?.email && (
                        <p className="text-xs text-gray-600">{appointment.client.person.email}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Team Member</p>
                      <Link to={`/technicians/${appointment.technician?.id}`} className="text-xs font-medium text-red-800 hover:underline">
                        {appointment.technician?.person?.first_name} {appointment.technician?.person?.last_name}
                      </Link>
                    </div>
                    {appointment.receiver && (
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Receiver</p>
                        <p className="text-xs font-medium text-gray-900">
                          {appointment.receiver.first_name} {appointment.receiver.last_name}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Location */}
                {appointment.address && (
                  <div className="border-t border-gray-200 pt-2">
                    <p className="text-xs text-gray-500 mb-0.5">Location</p>
                    <p className="text-xs text-gray-900">{appointment.address}</p>
                    {appointment.city && appointment.state && (
                      <p className="text-xs text-gray-900">{appointment.city}, {appointment.state} {appointment.zip_code}</p>
                    )}
                  </div>
                )}
              </div>
            </Panel>

            {/* Financial Information */}
            <Panel>
              <Section title="Financial Details">
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Exp Revenue</p>
                    <p className="text-xs font-semibold text-gray-900">{formatCurrency(appointment.expected_revenue)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Exp Profit</p>
                    <p className="text-xs font-semibold text-gray-900">{formatCurrency(appointment.expected_profit)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Discount</p>
                    <p className="text-xs font-semibold text-gray-900">{formatCurrency(appointment.discount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Total Payment</p>
                    <p className="text-xs font-semibold text-green-600">{formatCurrency(appointment.total_payment)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Payment Fee</p>
                    <p className="text-xs font-semibold text-gray-900">{formatCurrency(appointment.payment_fee)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Team Tip</p>
                    <p className="text-xs font-semibold text-gray-900">{formatCurrency(appointment.technician_tip)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Team Payout</p>
                    <p className="text-xs font-semibold text-gray-900">{formatCurrency(appointment.technician_payout)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Status</p>
                    {appointment.is_paid ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="success" small>Paid</Badge>
                        <button
                          onClick={async () => {
                            setSendingReceipt(true);
                            setReceiptError(null);
                            try {
                              await apiService.post('/amn/payments/send-receipt/', { appointment_id: appointment.id });
                              setReceiptSent(true);
                              setTimeout(() => setReceiptSent(false), 5000);
                            } catch (err) {
                              setReceiptError(err.message || 'Failed to send receipt');
                              setTimeout(() => setReceiptError(null), 5000);
                            } finally {
                              setSendingReceipt(false);
                            }
                          }}
                          disabled={sendingReceipt}
                          className="flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                        >
                          <Mail className="w-3 h-3" />
                          {sendingReceipt ? 'Sending...' : receiptSent ? 'Receipt Sent ✓' : 'Send Receipt'}
                        </button>
                        {receiptError && (
                          <span className="text-xs text-red-600">{receiptError}</span>
                        )}
                        {appointment.client?.has_card_on_file && (
                          <button
                            onClick={() => {
                              setChargeAmount('0');
                              setChargeTip('');
                              setChargeResult(null);
                              setShowChargeModal(true);
                            }}
                            className="flex items-center gap-1 px-2 py-0.5 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors"
                          >
                            <CreditCard className="w-3 h-3" />
                            Add Tip
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="error" small>Unpaid</Badge>
                        {appointment.client && (
                          <button
                            onClick={() => setShowCheckout(true)}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs bg-green-800 text-white rounded-md hover:bg-green-900 transition-colors font-medium shadow-sm"
                          >
                            <DollarSign className="w-3.5 h-3.5" />
                            Checkout
                          </button>
                        )}
                        {appointment.client?.has_card_on_file ? (
                          <button
                            onClick={() => {
                              setChargeAmount(appointment.expected_revenue || '');
                              setChargeTip('');
                              setChargeResult(null);
                              setShowChargeModal(true);
                            }}
                            className="flex items-center gap-1 px-2 py-0.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                          >
                            <CreditCard className="w-3 h-3" />
                            Charge {appointment.client.card_brand} ****{appointment.client.card_last_four}
                          </button>
                        ) : appointment.client ? (
                          <button
                            onClick={() => setShowAddCard(true)}
                            className="flex items-center gap-1 px-2 py-0.5 text-xs bg-red-800 text-white rounded hover:bg-red-900 transition-colors"
                          >
                            <CreditCard className="w-3 h-3" />
                            Add Card
                          </button>
                        ) : null}
                        <Button
                          onClick={fetchCandidateTransactions}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-xs py-0.5 px-2"
                          disabled={loadingCandidates}
                        >
                          {loadingCandidates ? 'Loading...' : 'Find Payment'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Section>
            </Panel>

            {/* Online Booking */}
            <Panel>
              <Section title="Online Booking">
                {appointment.online_booking_info ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs text-gray-500">Booked by:</span>
                      <span className="text-xs font-medium text-gray-900">{appointment.online_booking_info.client_name}</span>
                      <span className="text-xs text-gray-500">Paid:</span>
                      <span className="text-xs font-semibold text-green-600">${appointment.online_booking_info.subtotal.toFixed(2)}</span>
                      {appointment.online_booking_info.balance > 0 && (
                        <>
                          <span className="text-xs text-gray-500">Balance:</span>
                          <span className="text-xs font-semibold text-red-600">${appointment.online_booking_info.balance.toFixed(2)}</span>
                        </>
                      )}
                      <button
                        onClick={async () => {
                          if (window.confirm('Unlink this online booking?')) {
                            await apiService.unlinkBookingFromAppointment(id);
                            fetchAppointmentDetails();
                          }
                        }}
                        className="text-[10px] text-gray-400 hover:text-red-600 ml-auto"
                      >
                        Unlink
                      </button>
                    </div>
                    {appointment.online_booking_info.balance > 0 && (
                      <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                        <button
                          disabled={chargingBalance}
                          onClick={async () => {
                            const tip = parseFloat(balanceTip || 0);
                            const balance = appointment.online_booking_info.balance;
                            const total = balance + tip;
                            if (!window.confirm(`Charge $${total.toFixed(2)} (balance: $${balance.toFixed(2)}${tip > 0 ? `, tip: $${tip.toFixed(2)}` : ''})?`)) return;
                            setChargingBalance(true);
                            try {
                              await apiService.chargeBalance(id, { tip });
                              setBalanceTip('');
                              fetchAppointmentDetails();
                            } catch (err) {
                              alert('Charge failed: ' + (err.message || err));
                            } finally {
                              setChargingBalance(false);
                            }
                          }}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-medium"
                        >
                          <CreditCard className="w-3 h-3" />
                          {chargingBalance ? 'Charging...' : `Charge Balance $${appointment.online_booking_info.balance.toFixed(2)}`}
                        </button>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Tip"
                          value={balanceTip}
                          onChange={e => setBalanceTip(e.target.value)}
                          className="w-16 px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-green-600"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">No online booking linked</span>
                    <button
                      disabled={loadingBookings}
                      onClick={async () => {
                        setLoadingBookings(true);
                        try {
                          const data = await apiService.getUnlinkedBookings(id);
                          setUnlinkedBookings(data.bookings || []);
                          setShowLinkBooking(true);
                        } catch (err) {
                          alert('Failed to load bookings: ' + err.message);
                        } finally {
                          setLoadingBookings(false);
                        }
                      }}
                      className="px-2 py-0.5 text-xs bg-purple-700 text-white rounded hover:bg-purple-800 transition-colors"
                    >
                      {loadingBookings ? 'Loading...' : 'Link Booking'}
                    </button>
                  </div>
                )}
              </Section>
            </Panel>

            {/* Link Booking Panel */}
            {showLinkBooking && (
              <Panel noPadding>
                <div className="px-2 py-1.5 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-xs font-medium text-gray-900">
                    Select Online Booking to Link ({unlinkedBookings.length})
                  </h2>
                  <Button onClick={() => setShowLinkBooking(false)} size="sm" variant="secondary" className="text-xs py-0.5 px-2">
                    Close
                  </Button>
                </div>
                {unlinkedBookings.length === 0 ? (
                  <p className="text-xs text-gray-500 p-3 text-center">No unlinked bookings found</p>
                ) : (
                  <div className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
                    {unlinkedBookings.map(booking => (
                      <div key={booking.id} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
                        <div>
                          <p className="text-xs font-medium text-gray-900">{booking.client_name}</p>
                          <p className="text-[10px] text-gray-500">
                            {booking.requested_date} · ${booking.subtotal.toFixed(2)} · {booking.client_phone}
                          </p>
                        </div>
                        <button
                          disabled={linkingBooking}
                          onClick={async () => {
                            setLinkingBooking(true);
                            try {
                              await apiService.linkBookingToAppointment(id, booking.id);
                              setShowLinkBooking(false);
                              fetchAppointmentDetails();
                            } catch (err) {
                              alert('Failed to link: ' + (err.message || err));
                            } finally {
                              setLinkingBooking(false);
                            }
                          }}
                          className="px-2 py-0.5 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                        >
                          {linkingBooking ? '...' : 'Link'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            )}

            {/* E-Gift Card Section */}
            <Panel noPadding>
              <Section title="E-Gift Card">
                {/* Applied gift cards */}
                {appointment.gift_cards_applied && appointment.gift_cards_applied.length > 0 && (
                  <div className="space-y-1 mb-2">
                    {appointment.gift_cards_applied.map(gc => (
                      <div key={gc.id} className="flex items-center justify-between px-3 py-1.5 bg-green-50 rounded">
                        <div>
                          <span className="text-xs font-medium text-gray-900">{gc.gift_card_name}</span>
                          <span className="text-[10px] text-gray-500 ml-2">from {gc.sender_name} → {gc.recipient_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-green-600">${gc.amount.toFixed(2)}</span>
                          <button
                            onClick={async () => {
                              if (!window.confirm('Remove this gift card?')) return;
                              try {
                                await apiService.removeGiftCard(id, gc.id);
                                fetchAppointmentDetails();
                              } catch (err) {
                                alert('Failed: ' + (err.message || err));
                              }
                            }}
                            className="text-[10px] text-red-500 hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Apply button */}
                <button
                  onClick={async () => {
                    if (showGiftCards) {
                      setShowGiftCards(false);
                      return;
                    }
                    setLoadingGiftCards(true);
                    try {
                      const gcs = await apiService.getUnredeemedGiftCards();
                      setUnredeemedGiftCards(gcs);
                      setShowGiftCards(true);
                    } catch (err) {
                      alert('Failed to load gift cards');
                    } finally {
                      setLoadingGiftCards(false);
                    }
                  }}
                  className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                >
                  {loadingGiftCards ? 'Loading...' : showGiftCards ? 'Close' : '+ Apply Gift Card'}
                </button>
              </Section>
            </Panel>

            {/* Gift Card Selection Panel */}
            {showGiftCards && (
              <Panel noPadding>
                <div className="px-2 py-1.5 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-xs font-medium text-gray-900">
                    Select Gift Card ({unredeemedGiftCards.length})
                  </h2>
                  <button onClick={() => setShowGiftCards(false)} className="text-xs text-gray-500 hover:text-gray-700 py-0.5 px-2">
                    Close
                  </button>
                </div>
                {unredeemedGiftCards.length === 0 ? (
                  <p className="text-xs text-gray-500 p-3 text-center">No unredeemed gift cards</p>
                ) : (
                  <div className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
                    {unredeemedGiftCards.map(gc => (
                      <div key={gc.id} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
                        <div>
                          <p className="text-xs font-medium text-gray-900">{gc.gift_card_name} — ${gc.amount.toFixed(2)}</p>
                          <p className="text-[10px] text-gray-500">
                            To: {gc.recipient_name} · From: {gc.sender_name}
                          </p>
                        </div>
                        <button
                          disabled={applyingGiftCard}
                          onClick={async () => {
                            setApplyingGiftCard(true);
                            try {
                              await apiService.applyGiftCard(id, gc.id);
                              setShowGiftCards(false);
                              fetchAppointmentDetails();
                            } catch (err) {
                              alert('Failed: ' + (err.message || err));
                            } finally {
                              setApplyingGiftCard(false);
                            }
                          }}
                          className="px-2 py-0.5 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          {applyingGiftCard ? '...' : 'Apply'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            )}

            {/* Find Payment Panel */}
            {showFindPayment && (
              <Panel noPadding>
                <div className="px-2 py-1.5 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-xs font-medium text-gray-900">
                    Select Payment to Link ({candidateTransactions.length} candidates)
                  </h2>
                  <Button
                    onClick={() => setShowFindPayment(false)}
                    size="sm"
                    variant="secondary"
                    className="text-xs py-0.5 px-2"
                  >
                    Close
                  </Button>
                </div>
                {candidateTransactions.length === 0 ? (
                  <div className="p-3 text-xs text-gray-500 text-center">
                    No unmatched transactions found within 30 days of this appointment.
                  </div>
                ) : (
                  <div className="overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                          <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase">Match</th>
                          <th className="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase">Link</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {candidateTransactions.map((txn) => (
                          <tr key={txn.id} className={txn.same_client ? 'bg-green-50' : ''}>
                            <td className="px-2 py-1 text-xs text-gray-900">
                              {formatDate(txn.date_time)}
                            </td>
                            <td className="px-2 py-1 text-xs text-gray-900">
                              {txn.client_name}
                            </td>
                            <td className="px-2 py-1 text-xs text-gray-900 text-right font-medium">
                              {formatCurrency(txn.amount)}
                            </td>
                            <td className="px-2 py-1 text-center">
                              {txn.same_client && (
                                <Badge variant="success" small>Same Client</Badge>
                              )}
                            </td>
                            <td className="px-2 py-1 text-center">
                              <button
                                onClick={() => linkTransaction(txn.id)}
                                className="text-green-600 hover:text-green-800 font-medium text-xs"
                              >
                                Link
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            )}

            {/* Services */}
            <Panel noPadding>
              <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">Services</h2>
                <button
                  onClick={async () => {
                    if (allServicesList.length === 0) {
                      try {
                        const data = await apiService.get('/amn/services/', { active: true, ordering: 'name', page_size: 100 });
                        setAllServicesList(data.results || data);
                      } catch (err) { console.error(err); }
                    }
                    setShowAddService(!showAddService);
                    setAddServiceMode('catalog');
                  }}
                  className="px-3 py-1.5 text-xs font-medium bg-red-800 text-white rounded-md hover:bg-red-900 transition-colors"
                >+ Add Service</button>
              </div>
              {showAddService && (
                <div className="border-b border-gray-200">
                  {/* Tabs: Catalog / Custom */}
                  <div className="flex border-b border-gray-200">
                    <button onClick={() => setAddServiceMode('catalog')}
                      className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                        addServiceMode === 'catalog' ? 'text-red-800 border-b-2 border-red-800 bg-red-50' : 'text-gray-500 hover:text-gray-700'
                      }`}>From Catalog</button>
                    <button onClick={() => setAddServiceMode('custom')}
                      className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                        addServiceMode === 'custom' ? 'text-blue-700 border-b-2 border-blue-700 bg-blue-50' : 'text-gray-500 hover:text-gray-700'
                      }`}>Custom Service</button>
                  </div>

                  {addServiceMode === 'custom' ? (
                    <div className="px-3 py-3 bg-blue-50/50 space-y-2">
                      <input type="text" value={customService.description}
                        onChange={e => setCustomService({...customService, description: e.target.value})}
                        placeholder="Service description"
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500" autoFocus />
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] text-gray-500 font-medium mb-0.5">Client Cost ($)</label>
                          <input type="number" step="0.01" value={customService.cost}
                            onChange={e => setCustomService({...customService, cost: e.target.value})}
                            placeholder="0.00"
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-500 font-medium mb-0.5">Team Member Split ($)</label>
                          <input type="number" step="0.01" value={customService.split}
                            onChange={e => setCustomService({...customService, split: e.target.value})}
                            placeholder="0.00"
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500" />
                        </div>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button disabled={!customService.description || !customService.cost || addingService}
                          onClick={async () => {
                            setAddingService(true);
                            try {
                              await apiService.post(`/amn/appointments/${id}/add-custom-service/`, {
                                description: customService.description,
                                cost: parseFloat(customService.cost),
                                split_amount: parseFloat(customService.split || 0),
                              });
                              setShowAddService(false);
                              setCustomService({ description: '', cost: '', split: '' });
                              fetchAppointmentDetails();
                            } catch (err) { alert('Failed: ' + err.message); }
                            finally { setAddingService(false); }
                          }}
                          className="flex-1 px-3 py-2 text-xs font-medium bg-blue-700 text-white rounded-md disabled:opacity-50 hover:bg-blue-800">
                          {addingService ? 'Adding...' : 'Add Custom Service'}
                        </button>
                        <button onClick={() => setShowAddService(false)}
                          className="px-3 py-2 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="px-3 py-3 flex items-center gap-2 flex-wrap">
                      <select value={addServiceId} onChange={e => setAddServiceId(e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800">
                        <option value="">Select service...</option>
                        {allServicesList.map(s => (
                          <option key={s.id} value={s.id}>{s.name} (${parseFloat(s.price || 0).toFixed(2)})</option>
                        ))}
                      </select>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setAddServiceQty(Math.max(1, addServiceQty - 1))} className="w-6 h-6 rounded bg-gray-200 text-xs flex items-center justify-center hover:bg-gray-300">−</button>
                        <span className="text-sm font-semibold w-5 text-center">{addServiceQty}</span>
                        <button onClick={() => setAddServiceQty(addServiceQty + 1)} className="w-6 h-6 rounded bg-gray-200 text-xs flex items-center justify-center hover:bg-gray-300">+</button>
                      </div>
                      <button
                        disabled={!addServiceId || addingService}
                        onClick={async () => {
                          setAddingService(true);
                          try {
                            await apiService.post(`/amn/appointments/${id}/add-service/`, { service_id: parseInt(addServiceId), quantity: addServiceQty });
                            setShowAddService(false);
                            setAddServiceId('');
                            setAddServiceQty(1);
                            fetchAppointmentDetails();
                          } catch (err) { alert('Failed: ' + err.message); }
                          finally { setAddingService(false); }
                        }}
                        className="px-3 py-2 text-xs font-medium bg-red-800 text-white rounded-md hover:bg-red-900 disabled:opacity-50"
                      >{addingService ? 'Adding...' : 'Add'}</button>
                    </div>
                  )}
                </div>
              )}
              {appointment.services && appointment.services.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                        <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
                        <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase">Split</th>
                        <th className="px-2 py-1 w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {appointment.services.map((service, index) => (
                        <tr key={service.id || index}>
                          <td className="px-2 py-1 text-xs text-gray-900">
                            {service.service_name || service.service?.name || 'Unknown Service'}
                          </td>
                          <td className="px-2 py-1 text-xs text-gray-900 text-right">
                            {formatCurrency(service.service_cost)}
                          </td>
                          <td className="px-2 py-1 text-xs text-gray-900 text-right">
                            {formatCurrency(service.split_amount)}
                          </td>
                          <td className="px-2 py-1 text-right">
                            <button
                              onClick={async () => {
                                if (window.confirm('Remove this service?')) {
                                  try {
                                    await apiService.post(`/amn/appointments/${id}/remove-service/`, { service_entry_id: service.id });
                                    fetchAppointmentDetails();
                                  } catch (err) { alert('Failed: ' + err.message); }
                                }
                              }}
                              className="text-gray-300 hover:text-red-500 text-[10px]"
                              title="Remove"
                            >✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="px-2 py-3 text-xs text-gray-400 text-center">No services</p>
              )}
            </Panel>

            {/* Notes & Actions */}
            <Panel>
              <Section title="Notes & Actions">
                <div className="space-y-2">
                  {/* Editable Additional Note */}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Additional Note</p>
                    <textarea
                      value={additionalNote}
                      onChange={(e) => setAdditionalNote(e.target.value)}
                      className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                      rows="3"
                      placeholder="Add notes about this appointment..."
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-1.5 pt-1">
                    <Button onClick={saveNote} disabled={isSavingNote} size="sm">
                      {isSavingNote ? 'Saving...' : (appointment.additional_note ? 'Update Note' : 'Save Note')}
                    </Button>
                    <Button onClick={cancelAppointment} size="sm" className="bg-red-600 hover:bg-red-700">
                      Cancel {appointment.square_id ? '(Square + Dashboard)' : '(Dashboard)'}
                    </Button>
                  </div>
                </div>
              </Section>
            </Panel>

            {/* Office Notes (internal) */}
            <Panel className="border-amber-200 bg-amber-50/30">
              <Section title="Office Notes (Internal)">
                <div className="space-y-2">
                  <textarea
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border border-amber-200 rounded focus:outline-none focus:ring-1 focus:ring-amber-500 bg-white"
                    rows="3"
                    placeholder="Scheduling changes, client communications, reassignment details..."
                  />
                  <Button onClick={saveInternalNotes} disabled={isSavingInternal} size="sm" variant="secondary">
                    {isSavingInternal ? 'Saving...' : (appointment.internal_notes ? 'Update Office Notes' : 'Save Office Notes')}
                  </Button>
                </div>
              </Section>
            </Panel>

            {/* Payments */}
            {appointment.transactions && appointment.transactions.length > 0 && (
              <Panel noPadding>
                <div className="px-2 py-1.5 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-xs font-medium text-gray-900">Payments</h2>
                  <div className="flex gap-1">
                    {appointment.transactions.length > 0 && parseFloat(appointment.total_payment) > parseFloat(appointment.expected_revenue) && (
                      <Button onClick={attributeDifference} size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs">
                        Attribute to Tip
                      </Button>
                    )}
                    {appointment.transactions.length > 0 && parseFloat(appointment.total_payment) < parseFloat(appointment.expected_revenue) && (
                      <Button onClick={attributeDifference} size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs">
                        Attribute to Discount
                      </Button>
                    )}
                    <Button onClick={() => setShowAdditionalPayments(!showAdditionalPayments)} size="sm" className="bg-green-600 hover:bg-green-700 text-xs">
                      {showAdditionalPayments ? 'Hide' : 'Add'}
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                        <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase">Tip</th>
                        <th className="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase">Remove</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {appointment.transactions.map((transaction, index) => (
                        <tr key={index}>
                          <td className="px-2 py-1 text-xs text-gray-900">
                            {formatDate(transaction.date_time)}
                          </td>
                          <td className="px-2 py-1 text-xs text-gray-900">
                            {transaction.client && (
                              <Link to={`/clients/${transaction.client.id}`} className="text-red-800 hover:underline">
                                {transaction.client.person?.first_name} {transaction.client.person?.last_name}
                              </Link>
                            )}
                          </td>
                          <td className="px-2 py-1 text-xs text-gray-900 text-right">
                            {formatCurrency(transaction.amount)}
                          </td>
                          <td className="px-2 py-1 text-xs text-gray-900 text-right">
                            {formatCurrency(transaction.tip)}
                          </td>
                          <td className="px-2 py-1 text-center">
                            <button
                              onClick={() => unmatchTransaction(transaction.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            )}

            {/* Add Payment - Possible Transactions */}
            {showAdditionalPayments && appointment.possible_transactions && appointment.possible_transactions.length > 0 && (
              <Panel noPadding>
                <div className="px-2 py-1.5 border-b border-gray-200">
                  <h2 className="text-xs font-medium text-gray-900">Select Payment to Add</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                        <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase">Tip</th>
                        <th className="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase">Add</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {appointment.possible_transactions
                        .sort((a, b) => new Date(b.date_time) - new Date(a.date_time))
                        .map((transaction, index) => (
                          <tr key={index}>
                            <td className="px-2 py-1 text-xs text-gray-900">
                              {formatDate(transaction.date_time)}
                            </td>
                            <td className="px-2 py-1 text-xs text-gray-900">
                              {transaction.client && (
                                <Link to={`/clients/${transaction.client.id}`} className="text-red-800 hover:underline">
                                  {transaction.client.person?.first_name} {transaction.client.person?.last_name}
                                </Link>
                              )}
                            </td>
                            <td className="px-2 py-1 text-xs text-gray-900 text-right">
                              {formatCurrency(transaction.amount)}
                            </td>
                            <td className="px-2 py-1 text-xs text-gray-900 text-right">
                              {formatCurrency(transaction.tip)}
                            </td>
                            <td className="px-2 py-1 text-center">
                              <button
                                onClick={() => matchTransaction(transaction.id)}
                                className="text-green-600 hover:text-green-800"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            )}

            {/* Event Technicians */}
            {(appointment.is_event || appointment.appointment_type === 'Event') && (
              <Panel>
                <Section title="Event Team Members">
                  <div className="space-y-2">
                    {(appointment.event_technicians || []).map((eventTech) => (
                      <div key={eventTech.id} className="flex items-center gap-2 border-b border-gray-200 pb-2 last:border-0">
                        <Link
                          to={`/technicians/${eventTech.technician.id}`}
                          className="text-sm font-medium text-red-800 hover:underline flex-shrink-0"
                        >
                          {eventTech.technician.person?.first_name} {eventTech.technician.person?.last_name}
                        </Link>
                        <div className="flex items-center gap-1 ml-auto">
                          <span className="text-xs text-gray-500">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="w-20 text-sm border border-gray-300 rounded px-1.5 py-0.5 text-right"
                            value={editingPayouts[eventTech.id] !== undefined ? editingPayouts[eventTech.id] : (eventTech.payout || '')}
                            onChange={(e) => setEditingPayouts(prev => ({ ...prev, [eventTech.id]: e.target.value }))}
                          />
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-xs py-0.5 px-2"
                            disabled={savingPayout === eventTech.id || editingPayouts[eventTech.id] === undefined}
                            onClick={() => saveEventTechPayout(eventTech.id, editingPayouts[eventTech.id])}
                          >
                            {savingPayout === eventTech.id ? '...' : 'Save'}
                          </Button>
                          <Button
                            size="sm"
                            className="bg-red-600 hover:bg-red-700 text-xs py-0.5 px-2"
                            onClick={() => removeEventTechnician(eventTech.id)}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}

                    {appointment.event_technicians?.length === 0 && !showAddTechnician && (
                      <p className="text-xs text-gray-500">No team members assigned yet.</p>
                    )}

                    {showAddTechnician && (
                      <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                        <select
                          className="text-sm border border-gray-300 rounded px-1.5 py-1 flex-1"
                          value={newEventTech.technician_id}
                          onChange={(e) => setNewEventTech(prev => ({ ...prev, technician_id: e.target.value }))}
                        >
                          <option value="">Select team member...</option>
                          {technicians
                            .filter(t => !(appointment.event_technicians || []).some(et => et.technician.id === t.id))
                            .map(t => (
                              <option key={t.id} value={t.id}>
                                {t.person?.first_name} {t.person?.last_name}
                              </option>
                            ))}
                        </select>
                        <span className="text-xs text-gray-500">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Payout"
                          className="w-20 text-sm border border-gray-300 rounded px-1.5 py-0.5 text-right"
                          value={newEventTech.payout}
                          onChange={(e) => setNewEventTech(prev => ({ ...prev, payout: e.target.value }))}
                        />
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-xs py-0.5 px-2"
                          disabled={savingNewEventTech}
                          onClick={addEventTechnician}
                        >
                          {savingNewEventTech ? '...' : 'Save'}
                        </Button>
                        <Button
                          size="sm"
                          className="bg-gray-400 hover:bg-gray-500 text-xs py-0.5 px-2"
                          onClick={() => { setShowAddTechnician(false); setNewEventTech({ technician_id: '', payout: '' }); }}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}

                    {!showAddTechnician && (
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-xs py-0.5 px-2 mt-1"
                        onClick={() => { setShowAddTechnician(true); fetchTechnicians(); }}
                      >
                        + Add Team Member
                      </Button>
                    )}
                  </div>
                </Section>
              </Panel>
            )}

            {/* Technician Review */}
            {appointment.review && (
              <Panel>
                <Section title="Team Member Review">
                  <div className="space-y-2">
                    {appointment.review.experience_rating && (
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Experience Rating</p>
                        <p className="text-sm font-medium text-gray-900">{appointment.review.experience_rating}</p>
                      </div>
                    )}
                    {appointment.review.client_satisfaction && (
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Client Satisfaction</p>
                        <p className="text-sm font-medium text-gray-900">{appointment.review.client_satisfaction}</p>
                      </div>
                    )}
                    {appointment.review.receiver_name && (
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Receiver Name</p>
                        <p className="text-sm font-medium text-gray-900">{appointment.review.receiver_name}</p>
                      </div>
                    )}
                    {appointment.review.tip_received && (
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Tip Received</p>
                        <p className="text-sm font-medium text-gray-900">{appointment.review.tip_received}</p>
                      </div>
                    )}
                    {appointment.review.client_health && (
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Client Health</p>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{appointment.review.client_health}</p>
                      </div>
                    )}
                    {appointment.review.client_interest && (
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Client Interest</p>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{appointment.review.client_interest}</p>
                      </div>
                    )}
                    {appointment.review.additional_services && (
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Additional Services Requested</p>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{appointment.review.additional_services}</p>
                      </div>
                    )}
                    {appointment.review.experience_details && (
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Experience Details</p>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{appointment.review.experience_details}</p>
                      </div>
                    )}
                    {appointment.review.technician_takeaways && (
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Team Member Takeaways</p>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{appointment.review.technician_takeaways}</p>
                      </div>
                    )}
                    {appointment.review.review && (
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Review</p>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{appointment.review.review}</p>
                      </div>
                    )}
                  </div>
                </Section>
              </Panel>
            )}

            {/* Review Request Status */}
            {appointment.review_request_conversation && (
              <Panel>
                <Section title="Review Request">
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Status</p>
                      <Badge variant={
                        appointment.review_request_conversation.status === 'review_link_sent' ? 'success' :
                        appointment.review_request_conversation.status === 'completed' ? 'default' :
                        appointment.review_request_conversation.status === 'pending_rating' ? 'warning' :
                        'info'
                      }>
                        {appointment.review_request_conversation.status.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    {appointment.review_request_conversation.client_rating && (
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Client Rating</p>
                        <p className="text-sm font-medium text-gray-900">
                          {appointment.review_request_conversation.client_rating} / 5
                          {appointment.review_request_conversation.client_rating === 5 && ' ⭐'}
                        </p>
                      </div>
                    )}
                    {appointment.review_request_conversation.rating_received_at && (
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Replied At</p>
                        <p className="text-sm text-gray-900">
                          {new Date(appointment.review_request_conversation.rating_received_at).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </Section>
              </Panel>
            )}

            {/* Pictures */}
            {appointment.pictures && appointment.pictures.length > 0 && (
              <Panel>
                <Section title="Pictures">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                    {appointment.pictures.map((picture, index) => (
                      <div key={index} className="relative aspect-square">
                        <img
                          src={picture.image_url}
                          alt={`Appointment ${index + 1}`}
                          className="w-full h-full object-cover rounded border border-gray-200"
                        />
                      </div>
                    ))}
                  </div>
                </Section>
              </Panel>
            )}
          </div>
        )}
      </div>

      {/* Call Modal */}
      <CallModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        phoneNumber={appointment?.client?.person?.phone_number}
        personName={appointment?.client?.person ? `${appointment.client.person.first_name || ''} ${appointment.client.person.last_name || ''}`.trim() : null}
        personId={appointment?.client?.person?.id}
        apiService={apiService}
      />

      {/* Add Card Modal */}
      <AddCardModal
        isOpen={showAddCard}
        onClose={() => setShowAddCard(false)}
        clientId={appointment?.client?.id}
        clientName={appointment?.client_name || 'Client'}
        clientPhone={appointment?.client?.person?.phone_number}
        apiService={apiService}
        onCardSaved={() => { setShowAddCard(false); fetchAppointmentDetails(); }}
      />

      {/* Charge Modal */}
      {showChargeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2">
          <div className="bg-white border border-gray-200 shadow-lg max-w-sm w-full mx-4">
            <div className="bg-green-700 text-white px-3 py-2 flex items-center justify-between border-b border-green-800">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Charge Card
              </h3>
              <button onClick={() => setShowChargeModal(false)} className="text-white hover:text-gray-200">
                <span className="text-lg">&times;</span>
              </button>
            </div>

            <div className="p-3 space-y-3">
              {chargeResult ? (
                <div className={`p-3 rounded text-center ${chargeResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <p className={`text-sm font-medium ${chargeResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {chargeResult.message}
                  </p>
                  <button
                    onClick={() => { setShowChargeModal(false); fetchAppointmentDetails(); }}
                    className="mt-2 px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <div className="text-xs text-gray-600">
                    Charging <strong>{appointment?.client?.card_brand} ****{appointment?.client?.card_last_four}</strong> for {appointment?.client_name}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Service Amount ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={chargeAmount}
                      onChange={(e) => setChargeAmount(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-600"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tip ($) <span className="text-gray-400">optional</span></label>
                    <input
                      type="number"
                      step="0.01"
                      value={chargeTip}
                      onChange={(e) => setChargeTip(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-600"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="bg-gray-50 rounded p-2 text-xs">
                    <div className="flex justify-between">
                      <span>Service:</span>
                      <span>${parseFloat(chargeAmount || 0).toFixed(2)}</span>
                    </div>
                    {parseFloat(chargeTip || 0) > 0 && (
                      <div className="flex justify-between">
                        <span>Tip:</span>
                        <span>${parseFloat(chargeTip || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold border-t border-gray-200 mt-1 pt-1">
                      <span>Total:</span>
                      <span>${(parseFloat(chargeAmount || 0) + parseFloat(chargeTip || 0)).toFixed(2)}</span>
                    </div>
                  </div>

                  <button
                    onClick={async () => {
                      const totalCharge = parseFloat(chargeAmount || 0) + parseFloat(chargeTip || 0);
                      if (totalCharge <= 0) return;
                      setCharging(true);
                      try {
                        const result = await apiService.chargeClient(
                          appointment.client.id,
                          parseFloat(chargeAmount),
                          {
                            appointmentId: appointment.id,
                            tip: parseFloat(chargeTip || 0),
                            description: `${appointment.client_name} - Appointment #${appointment.id}`,
                          }
                        );
                        setChargeResult({ success: true, message: `Charged $${result.amount.toFixed(2)} successfully` });
                      } catch (err) {
                        setChargeResult({ success: false, message: err.message || 'Charge failed' });
                      } finally {
                        setCharging(false);
                      }
                    }}
                    disabled={charging || (parseFloat(chargeAmount || 0) + parseFloat(chargeTip || 0)) <= 0}
                    className="w-full px-3 py-2 text-xs bg-green-700 text-white rounded hover:bg-green-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <DollarSign className="w-3.5 h-3.5" />
                    {charging ? 'Processing...' : `Charge $${(parseFloat(chargeAmount || 0) + parseFloat(chargeTip || 0)).toFixed(2)}`}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={showCheckout}
        onClose={() => setShowCheckout(false)}
        appointment={appointment}
        onComplete={() => fetchAppointmentDetails()}
      />

    </div>
  );

}

export default AppointmentDetailPage;
